<template>
  <div class="appLibrary">
    <v-overlay
      v-if="overlay"
      class="align-center justify-center"
    >
      <v-progress-circular
        color="primary"
        indeterminate
        size="64"
      />
    </v-overlay>

    <div
      class="appList"
    >
      <v-row>
        <v-col
          cols="12"
          sm="8"
          class="pt-0"
        >
          <h1>
            {{ $t('appLibrary_overview.txt.app_library') }}
          </h1>
        </v-col>
        <v-col
          cols="12"
          sm="4"
          class="pt-0"
        >
          <div class="totals float-sm-right pl-8 pl-sm-0">
            <div
              v-if="monthlyTotalCost > 0 || yearlyTotalCost > 0"
              class="label"
            >
              {{ $t('services_overview.txt.total_cost') }}
            </div>
            <div>
              <span
                v-if="monthlyTotalCost > 0"
                class="total"
              >{{ totalMonthPrice | formatPrice(currency.symbol) }}
              </span>
              <span v-if="monthlyTotalCost > 0">{{
                $t('services_overview.txt.month')
              }}</span>
              <span v-if="monthlyTotalCost > 0 && yearlyTotalCost > 0">
                &
              </span>
              <span
                v-if="yearlyTotalCost > 0"
                class="total"
              >{{ totalAnnualPrice | formatPrice(currency.symbol) }}
              </span>
              <span v-if="yearlyTotalCost > 0">{{
                $t('services_overview.txt.year')
              }}</span>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row class="pl-3">
        <v-col
          cols="10"
          sm="6"
          class="ma-0 pa-0"
        >
          <v-text-field
            v-model="freeSearch"
            :placeholder="$t('vehicleApps_overview.txt.search_box_placeholder')"
            outlined
            dense
            filled
            class="searchBox"
          />
        </v-col>
        <v-col
          cols="1"
          sm="1"
          class="ma-0 pa-0"
        >
          <v-btn
            :disabled="true"
            class="add-athlete rounded-0"
          >
            <v-icon
              size="28"
              color="white"
            >
              mdi-magnify
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="loading">
        <v-col>
          <div

            class="loader"
          >
            <v-progress-circular
              indeterminate
              color="primary"
            />
          </div>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col v-if="!filteredApps || filteredApps.length == 0">
          <div class="no-results">
            <v-icon>
              mdi-checkbox-blank-badge
            </v-icon>
            <br>{{ $t('appLibrary_overview.txt.empty_search') }}
          </div>
        </v-col>
        <v-col
          v-for="libraryApp in filteredApps"
          v-else
          :key="libraryApp.id"
          cols="6"
          md="3"
          sm="4"
          xl="2"
        >
          <AppLibraryCard
            :library-app="libraryApp"
            @toggleSelection="updateSelection"
          />
        </v-col>
      </v-row>
    </div>
    <v-footer
      fixed
      color="white"
      class="elevation-2"
      height="60px"
    >
      <v-container class="py-0">
        <v-row class="results_footer">
          <v-col
            class="py-2 py-md-0 text-center text-md-left align-self-center d-flex justify-space-around"
            cols="12"
            md="6"
            lg="5"
            offset-lg="4"
          >
            <AppLibraryChoiceResume :app-list="appsToActivate">
              <template v-slot:caption-plural>
                {{ $t('appLibrary_overview.txt.apps_to_activate_plural') }}
              </template>
              <template v-slot:caption-singular>
                {{ $t('appLibrary_overview.txt.apps_to_activate_singular') }}
              </template>
            </AppLibraryChoiceResume>

            <AppLibraryChoiceResume :app-list="appsToDeactivate">
              <template v-slot:caption-plural>
                {{ $t('appLibrary_overview.txt.apps_to_deactivate_plural') }}
              </template>
              <template v-slot:caption-singular>
                {{ $t('appLibrary_overview.txt.apps_to_deactivate_singular') }}
              </template>
            </AppLibraryChoiceResume>
          </v-col>
          <v-col
            class="py-2 py-md-0 text-center text-md-right align-self-center"
            cols="12"
            md="6"
            lg="3"
          >
            <v-btn
              depressed
              large
              :disabled="!hasSelection"
              class="btn-proceed"
              :class="{ disabled : !hasSelection}"
              @click="submit()"
            >
              {{ $t('appLibrary_overview.txt.save_changes') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>
  </div>
</template>

<script>
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import { mapState, mapActions } from 'vuex'
import AppLibraryCard from '@/components/VehicleApps/AppLibraryCard.vue'
import AppLibraryChoiceResume from '@/components/VehicleApps/AppLibraryChoiceResume.vue'
import { ServicesMixin } from '@/mixins/ServicesMixin.js'
import appLibraryService from '@/helpers/appLibraryService'

export default {
  name: 'AppLibraryOverview',
  components: {
    AppLibraryCard,
    AppLibraryChoiceResume
  },
  mixins: [ServicesMixin],
  props: {},
  data() {
    return {
      appList: [],
      loading: false,
      freeSearch: '',
      filteredResult: [],
      appsToActivate: [],
      appsToDeactivate: [],
      overlay: false
    }
  },
  computed: {
    ...mapState('account', ['currency', 'detailsLoaded']),
    filteredApps() {
      const clearSearchTerm = `${this.freeSearch}`.trim().removeDiacritics()
      if (!clearSearchTerm) return this.appList

      const searchRx = new RegExp(RegExp.escape(clearSearchTerm), 'i')
      return this.appList.filter(app => searchRx.test(app.searchKeywords))
    },
    hasSelection() {
      return this.appsToActivate.length || this.appsToDeactivate.length
    }
  },
  watch: {
    detailsLoaded(newValue) {
      if (newValue) this.getLibraryApps()
    }
  },
  async created() {
    await this.getLibraryApps()
    this.renderTotals()
  },

  methods: {
    ...mapActions('notification', ['setSnackbar']),
    ...mapActions('trucks', ['getTrucksPerService']),
    ...mapActions('products', ['getCompanyService']),
    clearAppSelection() {
      this.appsToActivate = []
      this.appsToDeactivate = []
    },
    async getLibraryApps() {
      this.loading = true
      this.clearAppSelection()

      if (!this.detailsLoaded) {
        return false
      }

      let apps = []
      try {
        apps = await appLibraryService.getAccountApps()
      } catch (e) {
        this.setSnackbar({
          showing: true,
          type: 'error',
          text: this.$t('appLibrary_overview.txt.failed_loading')
        })
      }

      this.appList = apps
      this.appList.sort(this.sortAppList)

      this.loading = false
    },

    renderTotals(forceRefresh = false) {
      Promise.all([
        this.getTrucksPerService(forceRefresh),
        this.getCompanyService(forceRefresh)
      ]).then(([servicesList, companyService]) => {
        this.activeServices = servicesList || []
        this.calculateTotals(true)

        this.addCompanyTotals(companyService)
      })
    },

    sortAppList(app1, app2) {
      if (app1.name < app2.name) return -1
      if (app1.name > app2.name) return 1
      return 0
    },

    updateSelection(selectedApp) {
      const targetList = selectedApp.isActive
        ? 'appsToDeactivate'
        : 'appsToActivate'

      if (selectedApp.isChosen) {
        this[targetList].push(selectedApp)
      } else {
        this[targetList] = this[targetList].filter(a => a.id !== selectedApp.id)
      }
    },
    async submit() {
      this.overlay = true

      try {
        const activeApps = [
          ...this.appList
            .filter(app => app.isActive && !app.isChosen)
            .map(app => app.id),
          ...this.appsToActivate.map(app => app.id)
        ]

        const request = {
          appIds: activeApps
        }

        const response = await apiCalls.post(
          api.AppLibraryActivateApps,
          '',
          '',
          request
        )

        this.overlay = false

        if (response?.data === true) {
          this.setSnackbar({
            showing: true,
            type: 'success',
            text: this.$t('appLibrary_overview.txt.apps_activated_success')
          })
          this.renderTotals(true)
        } else {
          this.setSnackbar({
            showing: true,
            type: 'error',
            text: this.$t('appLibrary_overview.txt.apps_activated_failure')
          })
        }

        this.getLibraryApps()
      } catch (e) {
        this.overlay = false
        this.setSnackbar({
          showing: true,
          type: 'error',
          text: this.$t('appLibrary_overview.txt.apps_activated_failure')
        })

        console.error(e)
      }
    }
  }
}
</script>

<style lang="scss">
.appLibrary {
  .appList {
    margin-bottom: 80px;
  }
  .loader {
    text-align: center;
    padding-top: 48px;
  }
  h1 {
    margin-bottom: 22px;
    font-size: 3rem;
    font-weight: 300;
    line-height: normal;
    color: var(--v-dark-base);
    text-align: left;
  }
  .no-results {
    font-size: 1.375rem;
    line-height: normal;
    text-align: center;
    color: var(--v-bluegrey-base);
    .v-icon {
      font-size: 100px;
      color: var(--v-cloudy-base);
    }
  }
  .totals {
    .label {
      font-size: 0.75rem;
      font-weight: bold;
      text-transform: uppercase;
      color: var(--v-bluegrey-base);
    }
    .total {
      font-size: 1.5rem;
      font-weight: bold;
      color: var(--v-slate-base);
      span {
        font-size: 0.875rem;
      }
    }
  }
  .add-athlete {
    height: 40px !important;
    width: 100%;
    background-image: linear-gradient(to bottom, #266cab 0%, #01529c);
    border-radius: 0;
    box-shadow: none;

    &:disabled .v-icon {
      color: white !important;
    }
  }

  .searchBox fieldset {
    background: white;
    border-radius: 0;
    top: -4px;
  }

  .default-icon {
    background-color: white;
  }

  .apps-resume {
    font-size: 1rem;
  }
}
</style>
